import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Download, Trash2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { getToken } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function PrivacyDashboard() {
    const { toast } = useToast();
    const { t } = useI18n();
    const queryClient = useQueryClient();

    // Fetch user profile for privacy settings
    const { data: user, isLoading } = useQuery<any>({
        queryKey: ["/api/profiles/me"],
    });

    const [prefs, setPrefs] = useState({
        analytical: false,
        marketing: false,
        ads: false
    });

    useEffect(() => {
        if (user) {
            setPrefs({
                analytical: user.consent_analytical ?? false,
                marketing: user.consent_marketing ?? false,
                ads: user.consent_ads ?? false
            });
        }
    }, [user]);

    const consentMutation = useMutation({
        mutationFn: async (newPrefs: typeof prefs) => {
            const token = await getToken();
            const res = await fetch("/api/privacy/consent", {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(newPrefs)
            });
            if (!res.ok) throw new Error("Failed to update preferences");
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["/api/profiles/me"] });
            toast({
                title: t("privacy.dashboard.savedTitle") || "Instellingen opgeslagen",
                description: t("privacy.dashboard.savedDesc") || "Je privacyvoorkeuren zijn bijgewerkt."
            });
        }
    });

    const handleToggle = (key: keyof typeof prefs) => {
        const newPrefs = { ...prefs, [key]: !prefs[key] };
        setPrefs(newPrefs);
        consentMutation.mutate(newPrefs);
    };

    const handleExport = async () => {
        try {
            const token = await getToken();
            window.location.href = `/api/privacy/export-data?token=${token}`; // Use token as query param if needed, or fetch and create blob
            // Better: fetch and trigger download
            const res = await fetch("/api/privacy/export-data", {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vintmap-data.json`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            toast({ title: t("privacy.dashboard.exportStarted") || "Gegevensexport gestart" });
        } catch (e) {
            toast({ title: t("common.error"), variant: "destructive" });
        }
    };

    const deleteMutation = useMutation({
        mutationFn: async () => {
            const token = await getToken();
            const res = await fetch("/api/privacy/delete-account", {
                method: "POST",
                headers: { "Authorization": `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Delete failed");
            return res.json();
        },
        onSuccess: (data) => {
            toast({
                title: t("privacy.dashboard.deleteStarted") || "Verwijdering ingepland",
                description: data.message
            });
        }
    });

    if (isLoading) {
        return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="h-6 w-6 text-primary" />
                        <CardTitle>{t("privacy.dashboard.title")}</CardTitle>
                    </div>
                    <CardDescription>
                        {t("privacy.dashboard.desc")}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Consent Toggles */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                            <div className="space-y-0.5">
                                <Label className="text-base">{t("cookie.functional")}</Label>
                                <p className="text-sm text-muted-foreground">{t("privacy.functional.desc")}</p>
                            </div>
                            <Switch checked disabled />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                            <div className="space-y-0.5">
                                <Label className="text-base">{t("cookie.analytical")}</Label>
                                <p className="text-sm text-muted-foreground">{t("cookie.analytical.desc")}</p>
                            </div>
                            <Switch
                                checked={prefs.analytical}
                                onCheckedChange={() => handleToggle("analytical")}
                                disabled={consentMutation.isPending}
                            />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                            <div className="space-y-0.5">
                                <Label className="text-base">{t("cookie.marketing")}</Label>
                                <p className="text-sm text-muted-foreground">{t("cookie.marketing.desc")}</p>
                            </div>
                            <Switch
                                checked={prefs.marketing}
                                onCheckedChange={() => handleToggle("marketing")}
                                disabled={consentMutation.isPending}
                            />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                            <div className="space-y-0.5">
                                <Label className="text-base">{t("privacy.dashboard.ads")}</Label>
                                <p className="text-sm text-muted-foreground">{t("privacy.dashboard.adsDesc")}</p>
                            </div>
                            <Switch
                                checked={prefs.ads}
                                onCheckedChange={() => handleToggle("ads")}
                                disabled={consentMutation.isPending}
                            />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
                            <div className="space-y-0.5">
                                <Label className="text-base">{t("privacy.locationConsent")}</Label>
                                <p className="text-sm text-muted-foreground">{t("privacy.locationConsentDesc")}</p>
                            </div>
                            <Switch
                                checked={user?.consent_location ?? false}
                                onCheckedChange={(checked) => {
                                    consentMutation.mutate({ ...prefs, location: checked } as any);
                                }}
                                disabled={consentMutation.isPending}
                            />
                        </div>

                        <div className="p-4 bg-muted/30 rounded-lg border space-y-3">
                            <div className="space-y-0.5">
                                <Label className="text-base">{t("privacy.dashboard.geotargeting")}</Label>
                                <p className="text-sm text-muted-foreground">{t("privacy.dashboard.geotargetingDesc")}</p>
                            </div>
                            <div className="flex gap-2">
                                {["national", "provincial", "local"].map((level) => (
                                    <Button
                                        key={level}
                                        variant={user?.ad_preferences?.geotargeting === level ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => {
                                            consentMutation.mutate({
                                                ...prefs,
                                                ad_preferences: { ...user?.ad_preferences, geotargeting: level }
                                            } as any);
                                        }}
                                        className="capitalize"
                                    >
                                        {t(`privacy.dashboard.geotargeting.${level}`)}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t space-y-4">
                        <h3 className="font-semibold">{t("privacy.dashboard.dataRights")}</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                                <Button variant="outline" onClick={handleExport} className="justify-start gap-2">
                                    <Download className="h-4 w-4" />
                                    {t("privacy.dashboard.export")}
                                </Button>
                                <p className="text-xs text-muted-foreground">{t("privacy.export.desc")}</p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="outline" className="justify-start gap-2 text-destructive border-destructive/20 hover:bg-destructive/10">
                                            <Trash2 className="h-4 w-4" />
                                            {t("account.deleteAccount")}
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle className="text-destructive flex items-center gap-2">
                                                <AlertTriangle className="h-5 w-5" />
                                                {t("account.deleteConfirmTitle")}
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                                {t("account.deleteConfirmDesc")}
                                                <br /><br />
                                                <strong>{t("privacy.dashboard.deleteNote")}</strong>
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => deleteMutation.mutate()} className="bg-destructive text-white hover:bg-destructive/90">
                                                {t("privacy.dashboard.confirmDelete")}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                                <p className="text-xs text-muted-foreground">{t("privacy.delete.desc")}</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
